import { mutate } from 'swr';
import { useMemo } from 'react';

import axiosInstance from 'src/utils/axios';
import { authenticatedSwrOptions } from 'src/utils/constants';

import useAuthenticatedSWR from 'src/auth/hooks/use-authenticated-swr';

import { SystemEndpoints } from './systemEndpoints';

// Academic Year
//----------------------------------------------------------------------
export function useGetAcademicYears(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.AcademicYear.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  const memoizedValue = useMemo(
    () => ({
      academicYears: data || [],
      academicYearsLoading: isLoading,
      academicYearsError: error,
      academicYearsValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetAcademicYear(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.AcademicYear.getById(id) : null,
    authenticatedSwrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      academicYear: data || {},
      academicYearLoading: isLoading,
      academicYearError: error,
      academicYearValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useAcademicYearOperations() {
  const createAcademicYear = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.AcademicYear.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating academic year:', error);
      throw error;
    }
  };

  const updateAcademicYear = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.AcademicYear.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating academic year:', error);
      throw error;
    }
  };

  const deleteAcademicYear = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.AcademicYear.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting academic year:', error);
      throw error;
    }
  };

  return { createAcademicYear, updateAcademicYear, deleteAcademicYear };
}

// Address Type
//----------------------------------------------------------------------

export function useGetAddressTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.AddressType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      addressTypes: data || [],
      addressTypesLoading: isLoading,
      addressTypesError: error,
      addressTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetAddressType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.AddressType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      addressType: data || {},
      addressTypeLoading: isLoading,
      addressTypeError: error,
      addressTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useAddressTypeOperations() {
  const createAddressType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.AddressType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating address type:', error);
      throw error;
    }
  };

  const updateAddressType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.AddressType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating address type:', error);
      throw error;
    }
  };

  const deleteAddressType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.AddressType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting address type:', error);
      throw error;
    }
  };

  return { createAddressType, updateAddressType, deleteAddressType };
}

/// Age Interval
//----------------------------------------------------------------------

// Fetch age intervals by competition ID (standalone function)
export async function fetchAgeIntervalsByCompetitionId(competitionId) {
  try {
    const response = await axiosInstance.post(SystemEndpoints.AgeInterval.getAllWithFilterAdmin, {
      CompetitionId: competitionId,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching age intervals:', error);
    throw error;
  }
}

export function useGetAgeIntervals(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.AgeInterval.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      ageIntervals: data || [],
      ageIntervalsLoading: isLoading,
      ageIntervalsError: error,
      ageIntervalsValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetAgeInterval(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.AgeInterval.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      ageInterval: data || {},
      ageIntervalLoading: isLoading,
      ageIntervalError: error,
      ageIntervalValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useAgeIntervalOperations() {
  const createAgeInterval = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.AgeInterval.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating age interval:', error);
      throw error;
    }
  };

  const updateAgeInterval = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.AgeInterval.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating age interval:', error);
      throw error;
    }
  };

  const deleteAgeInterval = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.AgeInterval.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting age interval:', error);
      throw error;
    }
  };

  const duplicateAgeInterval = async (sourceCompetitionId, destinationCompetitionId) => {
    try {
      const response = await axiosInstance.get(
        SystemEndpoints.AgeInterval.duplicate(sourceCompetitionId, destinationCompetitionId)
      );
      return response.data;
    } catch (error) {
      console.error('Error duplicating age interval:', error);
      throw error;
    }
  };

  return { createAgeInterval, updateAgeInterval, deleteAgeInterval, duplicateAgeInterval };
}

// CertificateType
//----------------------------------------------------------------------
export function useGetCertificateTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.CertificateType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      certificateTypes: data || [],
      certificateTypesLoading: isLoading,
      certificateTypesError: error,
      certificateTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetCertificateType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.CertificateType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      certificateType: data || {},
      certificateTypeLoading: isLoading,
      certificateTypeError: error,
      certificateTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useCertificateTypeOperations() {
  const createCertificateType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.CertificateType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating certificate type:', error);
      throw error;
    }
  };

  const updateCertificateType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.CertificateType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating certificate type:', error);
      throw error;
    }
  };

  const deleteCertificateType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.CertificateType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting certificate type:', error);
      throw error;
    }
  };

  return { createCertificateType, updateCertificateType, deleteCertificateType };
}

// CompetitionType
//----------------------------------------------------------------------
export function useGetCompetitionTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.CompetitionType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      competitionTypes: data || [],
      competitionTypesLoading: isLoading,
      competitionTypesError: error,
      competitionTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetCompetitionType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.CompetitionType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      competitionType: data || {},
      competitionTypeLoading: isLoading,
      competitionTypeError: error,
      competitionTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useCompetitionTypeOperations() {
  const createCompetitionType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.CompetitionType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating competition type:', error);
      throw error;
    }
  };

  const updateCompetitionType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.CompetitionType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating competition type:', error);
      throw error;
    }
  };

  const deleteCompetitionType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.CompetitionType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting competition type:', error);
      throw error;
    }
  };

  return { createCompetitionType, updateCompetitionType, deleteCompetitionType };
}

// Disability Type
//----------------------------------------------------------------------

export function useGetDisabilityTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.DisabilityType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      disabilityTypes: data || [],
      disabilityTypesLoading: isLoading,
      disabilityTypesError: error,
      disabilityTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetDisabilityType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.DisabilityType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      disabilityType: data || {},
      disabilityTypeLoading: isLoading,
      disabilityTypeError: error,
      disabilityTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useDisabilityTypeOperations() {
  const createDisabilityType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.DisabilityType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating Disability Type:', error);
      throw error;
    }
  };

  const updateDisabilityType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.DisabilityType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating Disability Type:', error);
      throw error;
    }
  };

  const deleteDisabilityType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.DisabilityType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting Disability Type:', error);
      throw error;
    }
  };

  return { createDisabilityType, updateDisabilityType, deleteDisabilityType };
}

// DocumentFormatType
//----------------------------------------------------------------------
export function useGetDocumentFormatTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.DocumentFormatType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      documentFormatTypes: data || [],
      documentFormatTypesLoading: isLoading,
      documentFormatTypesError: error,
      documentFormatTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetDocumentFormatType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.DocumentFormatType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      documentFormatType: data || {},
      documentFormatTypeLoading: isLoading,
      documentFormatTypeError: error,
      documentFormatTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useDocumentFormatTypeOperations() {
  const createDocumentFormatType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.DocumentFormatType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating document format type:', error);
      throw error;
    }
  };

  const updateDocumentFormatType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.DocumentFormatType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating document format type:', error);
      throw error;
    }
  };

  const deleteDocumentFormatType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.DocumentFormatType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting document format type:', error);
      throw error;
    }
  };

  return { createDocumentFormatType, updateDocumentFormatType, deleteDocumentFormatType };
}
// DocumentType
//----------------------------------------------------------------------
export function useGetDocumentTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.DocumentType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      documentTypes: data || [],
      documentTypesLoading: isLoading,
      documentTypesError: error,
      documentTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetDocumentType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.DocumentType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      documentType: data || {},
      documentTypeLoading: isLoading,
      documentTypeError: error,
      documentTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useDocumentTypeOperations() {
  const createDocumentType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.DocumentType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating document type:', error);
      throw error;
    }
  };

  const updateDocumentType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.DocumentType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating document type:', error);
      throw error;
    }
  };

  const deleteDocumentType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.DocumentType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting document type:', error);
      throw error;
    }
  };

  return { createDocumentType, updateDocumentType, deleteDocumentType };
}

// EmailType
//----------------------------------------------------------------------
export function useGetEmailTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.EmailType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      emailTypes: data || [],
      emailTypesLoading: isLoading,
      emailTypesError: error,
      emailTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetEmailType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.EmailType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      emailType: data || {},
      emailTypeLoading: isLoading,
      emailTypeError: error,
      emailTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useEmailTypeOperations() {
  const createEmailType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.EmailType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating email type:', error);
      throw error;
    }
  };

  const updateEmailType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.EmailType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating email type:', error);
      throw error;
    }
  };

  const deleteEmailType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.EmailType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting email type:', error);
      throw error;
    }
  };

  return { createEmailType, updateEmailType, deleteEmailType };
}

// Gender
//----------------------------------------------------------------------
export function useGetGenders(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.Gender.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      genders: data || [],
      gendersLoading: isLoading,
      gendersError: error,
      gendersValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetGender(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.Gender.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      gender: data || {},
      genderLoading: isLoading,
      genderError: error,
      genderValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGenderOperations() {
  const createGender = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.Gender.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating gender:', error);
      throw error;
    }
  };

  const updateGender = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.Gender.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating gender:', error);
      throw error;
    }
  };

  const deleteGender = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.Gender.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting gender:', error);
      throw error;
    }
  };

  return { createGender, updateGender, deleteGender };
}

// Grade
//----------------------------------------------------------------------
export function useGetGrades(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.Grade.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      grades: data || [],
      gradesLoading: isLoading,
      gradesError: error,
      gradesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetGrade(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.Grade.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      grade: data || {},
      gradeLoading: isLoading,
      gradeError: error,
      gradeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGradeOperations() {
  const createGrade = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.Grade.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating grade:', error);
      throw error;
    }
  };

  const updateGrade = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.Grade.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating grade:', error);
      throw error;
    }
  };

  const deleteGrade = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.Grade.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting grade:', error);
      throw error;
    }
  };

  return { createGrade, updateGrade, deleteGrade };
}

// Language
//----------------------------------------------------------------------
export function useGetLanguages(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.Language.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      languages: data || [],
      languagesLoading: isLoading,
      languagesError: error,
      languagesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetLanguage(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.Language.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      language: data || {},
      languageLoading: isLoading,
      languageError: error,
      languageValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useLanguageOperations() {
  const createLanguage = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.Language.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating language:', error);
      throw error;
    }
  };

  const updateLanguage = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.Language.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating language:', error);
      throw error;
    }
  };

  const deleteLanguage = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.Language.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting language:', error);
      throw error;
    }
  };

  return { createLanguage, updateLanguage, deleteLanguage };
}

// PhoneType
//----------------------------------------------------------------------
export function useGetPhoneTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.PhoneType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      phoneTypes: data || [],
      phoneTypesLoading: isLoading,
      phoneTypesError: error,
      phoneTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetPhoneType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.PhoneType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      phoneType: data || {},
      phoneTypeLoading: isLoading,
      phoneTypeError: error,
      phoneTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function usePhoneTypeOperations() {
  const createPhoneType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.PhoneType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating phone type:', error);
      throw error;
    }
  };

  const updatePhoneType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.PhoneType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating phone type:', error);
      throw error;
    }
  };

  const deletePhoneType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.PhoneType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting phone type:', error);
      throw error;
    }
  };

  return { createPhoneType, updatePhoneType, deletePhoneType };
}

// ProjectFileType
//----------------------------------------------------------------------
export function useGetProjectFileTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.ProjectFileType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      projectFileTypes: data || [],
      projectFileTypesLoading: isLoading,
      projectFileTypesError: error,
      projectFileTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetProjectFileType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.ProjectFileType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      projectFileType: data || {},
      projectFileTypeLoading: isLoading,
      projectFileTypeError: error,
      projectFileTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useProjectFileTypeOperations() {
  const createProjectFileType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.ProjectFileType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating project file type:', error);
      throw error;
    }
  };

  const updateProjectFileType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.ProjectFileType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating project file type:', error);
      throw error;
    }
  };

  const deleteProjectFileType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.ProjectFileType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting project file type:', error);
      throw error;
    }
  };

  return { createProjectFileType, updateProjectFileType, deleteProjectFileType };
}

// Semester
//----------------------------------------------------------------------
export function useGetSemesters(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.Semester.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      semesters: data || [],
      semestersLoading: isLoading,
      semestersError: error,
      semestersValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetSemester(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.Semester.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      semester: data || {},
      semesterLoading: isLoading,
      semesterError: error,
      semesterValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useSemesterOperations() {
  const createSemester = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.Semester.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating semester:', error);
      throw error;
    }
  };

  const updateSemester = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.Semester.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating semester:', error);
      throw error;
    }
  };

  const deleteSemester = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.Semester.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting semester:', error);
      throw error;
    }
  };

  return { createSemester, updateSemester, deleteSemester };
}

// VisaType
//----------------------------------------------------------------------
export function useGetVisaTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.VisaType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      visaTypes: data || [],
      visaTypesLoading: isLoading,
      visaTypesError: error,
      visaTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetVisaType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.VisaType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      visaType: data || {},
      visaTypeLoading: isLoading,
      visaTypeError: error,
      visaTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useVisaTypeOperations() {
  const createVisaType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.VisaType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating visa type:', error);
      throw error;
    }
  };

  const updateVisaType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.VisaType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating visa type:', error);
      throw error;
    }
  };

  const deleteVisaType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.VisaType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting visa type:', error);
      throw error;
    }
  };

  return { createVisaType, updateVisaType, deleteVisaType };
}

// MealPreference
//----------------------------------------------------------------------

export function useGetMealPreferences(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.MealPreference.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      mealPreferences: data || [],
      mealPreferencesLoading: isLoading,
      mealPreferencesError: error,
      mealPreferencesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetMealPreference(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.MealPreference.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      mealPreference: data || {},
      mealPreferenceLoading: isLoading,
      mealPreferenceError: error,
      mealPreferenceValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useMealPreferenceOperations() {
  const createMealPreference = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.MealPreference.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating meal preference:', error);
      throw error;
    }
  };

  const updateMealPreference = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.MealPreference.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating meal preference:', error);
      throw error;
    }
  };

  const deleteMealPreference = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.MealPreference.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting meal preference:', error);
      throw error;
    }
  };

  return { createMealPreference, updateMealPreference, deleteMealPreference };
}

// ShirtSize
//----------------------------------------------------------------------

export function useGetShirtSizes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.ShirtSize.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      shirtSizes: data || [],
      shirtSizesLoading: isLoading,
      shirtSizesError: error,
      shirtSizesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetShirtSize(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.ShirtSize.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      shirtSize: data || {},
      shirtSizeLoading: isLoading,
      shirtSizeError: error,
      shirtSizeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useShirtSizeOperations() {
  const createShirtSize = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.ShirtSize.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating shirt size:', error);
      throw error;
    }
  };

  const updateShirtSize = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.ShirtSize.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating shirt size:', error);
      throw error;
    }
  };

  const deleteShirtSize = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.ShirtSize.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting shirt size:', error);
      throw error;
    }
  };

  return { createShirtSize, updateShirtSize, deleteShirtSize };
}

// PerformanceType
//----------------------------------------------------------------------
export function useGetPerformanceTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.PerformanceType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      performanceTypes: data || [],
      performanceTypesLoading: isLoading,
      performanceTypesError: error,
      performanceTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetPerformanceType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.PerformanceType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      performanceType: data || {},
      performanceTypeLoading: isLoading,
      performanceTypeError: error,
      performanceTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function usePerformanceTypeOperations() {
  const createPerformanceType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.PerformanceType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating performance type:', error);
      throw error;
    }
  };

  const updatePerformanceType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.PerformanceType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating performance type:', error);
      throw error;
    }
  };

  const deletePerformanceType = async (id) => {
    try {
      // Önce performans tipinin var olup olmadığını kontrol et
      const checkResponse = await axiosInstance
        .get(SystemEndpoints.PerformanceType.getById(id))
        .catch((error) => {
          if (error.response?.status === 404) {
            throw new Error('Performance type not found or already deleted');
          }
          throw error;
        });

      const response = await axiosInstance.delete(SystemEndpoints.PerformanceType.delete(id));

      mutate(SystemEndpoints.PerformanceType.getAll);
      return response.data;
    } catch (error) {
      console.error('Error deleting performance type:', error);

      // Detaylı hata mesajı
      if (error.response?.status === 500) {
        const errorMsg = error.response.data?.Message || 'Server error during deletion';
        if (errorMsg.includes('exists or deleted')) {
          throw new Error('Performance type not found or already deleted');
        } else {
          throw new Error('This performance type cannot be deleted because it is in use');
        }
      }

      throw error;
    }
  };

  return { createPerformanceType, updatePerformanceType, deletePerformanceType };
}

export function useGetTransportationTypes() {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.TransportationType.getAll,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      transportationTypes: data || [],
      transportationTypesLoading: isLoading,
      transportationTypesError: error,
      transportationTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useTransportationTypeOperations() {
  const createTransportationType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.TransportationType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating transportation type:', error);
      throw error;
    }
  };

  const updateTransportationType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.TransportationType.update(id), data);

      return response.data;
    } catch (error) {
      console.error('Error updating transportation type:', error);
      throw error;
    }
  };

  const deleteTransportationType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.TransportationType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting transportation type:', error);
      throw error;
    }
  };

  return { createTransportationType, updateTransportationType, deleteTransportationType };
}

// SustainableDevelopmentGoal
//----------------------------------------------------------------------
export function useGetSustainableDevelopmentGoals(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.SustainableDevelopmentGoal.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      sustainableDevelopmentGoals: data || [],
      sustainableDevelopmentGoalsLoading: isLoading,
      sustainableDevelopmentGoalsError: error,
      sustainableDevelopmentGoalsValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetSustainableDevelopmentGoal(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.SustainableDevelopmentGoal.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      sustainableDevelopmentGoal: data || {},
      sustainableDevelopmentGoalLoading: isLoading,
      sustainableDevelopmentGoalError: error,
      sustainableDevelopmentGoalValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useSustainableDevelopmentGoalOperations() {
  const createSustainableDevelopmentGoal = async (data) => {
    try {
      const response = await axiosInstance.post(
        SystemEndpoints.SustainableDevelopmentGoal.create,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error creating sustainable development goal:', error);
      throw error;
    }
  };

  const updateSustainableDevelopmentGoal = async (id, data) => {
    try {
      const response = await axiosInstance.put(
        SystemEndpoints.SustainableDevelopmentGoal.update(id),
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error updating sustainable development goal:', error);
      throw error;
    }
  };

  const deleteSustainableDevelopmentGoal = async (id) => {
    try {
      const response = await axiosInstance.delete(
        SystemEndpoints.SustainableDevelopmentGoal.delete(id)
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting sustainable development goal:', error);
      throw error;
    }
  };

  return {
    createSustainableDevelopmentGoal,
    updateSustainableDevelopmentGoal,
    deleteSustainableDevelopmentGoal,
  };
}

// Notification
//----------------------------------------------------------------------

export function useNotificationOperations() {
  const sendNotification = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.Notification.send, data);
      return response.data;
    } catch (error) {
      console.error('Error sending notifications:', error);
      throw error;
    }
  };

  const readNotification = async (id) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.Notification.read(id));
      return response.data;
    } catch (error) {
      console.error('Error updating notification:', error);
      throw error;
    }
  };

  const deleteNotification = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.Notification.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error updating notification:', error);
      throw error;
    }
  };

  return { sendNotification, readNotification, deleteNotification };
}

// FeedbackType
//----------------------------------------------------------------------
export function useGetFeedbackTypes(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    SystemEndpoints.FeedbackType.getAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      feedbackTypes: data || [],
      feedbackTypesLoading: isLoading,
      feedbackTypesError: error,
      feedbackTypesValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetFeedbackType(id) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    id ? SystemEndpoints.FeedbackType.getById(id) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      feedbackType: data || {},
      feedbackTypeLoading: isLoading,
      feedbackTypeError: error,
      feedbackTypeValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useFeedbackTypeOperations() {
  const createFeedbackType = async (data) => {
    try {
      const response = await axiosInstance.post(SystemEndpoints.FeedbackType.create, data);
      return response.data;
    } catch (error) {
      console.error('Error creating feedback type:', error);
      throw error;
    }
  };

  const updateFeedbackType = async (id, data) => {
    try {
      const response = await axiosInstance.put(SystemEndpoints.FeedbackType.update(id), data);
      return response.data;
    } catch (error) {
      console.error('Error updating feedback type:', error);
      throw error;
    }
  };

  const deleteFeedbackType = async (id) => {
    try {
      const response = await axiosInstance.delete(SystemEndpoints.FeedbackType.delete(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting feedback type:', error);
      throw error;
    }
  };

  return { createFeedbackType, updateFeedbackType, deleteFeedbackType };
}
